import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import LandingBuilder from '../components/LandingBuilder/LandingBuilder';
import { getTemplate } from '../components/LandingBuilder/templates';
import { buildSocialPlan, buildVisualSVG, svgToDataUri, downloadSvgAsPng } from './socialVisual';
import './Acquisition.css';

// URL distante → data-URI (pour intégrer photo/logo dans le SVG sans salir le canvas)
function urlToDataUri(url) {
  if (!url) return Promise.resolve('');
  return fetch(url, { mode: 'cors' })
    .then(r => r.ok ? r.blob() : Promise.reject())
    .then(b => new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(''); fr.readAsDataURL(b); }))
    .catch(() => '');
}

/**
 * 🧲 Campagne d'acquisition — « la base d'adresses est de l'or ».
 * Maquette validée 23.07 : assistant 3 questions → landing concours générée
 * (éditable) → amplification réseaux avec contenus prêts par objectif.
 * Chaque participant = contact consenti (horodaté nLPD) avec anniversaire.
 */

const PRIZE_SUGGESTIONS = [
  { kw: ['garage', 'auto', 'carrosser', 'pneu', 'moto'], prizes: ['🔧 Un contrôle hivernal offert', '🚗 Un bon d\'atelier de 100 CHF', '🧽 Un nettoyage complet offert'] },
  { kw: ['resto', 'restaur', 'café', 'pizz', 'brasserie', 'traiteur'], prizes: ['🍽️ Un repas pour 2 personnes', '🍾 Un apéro pour 4', '🎂 Un menu découverte offert'] },
  { kw: ['coiff', 'beaut', 'esthet', 'institut', 'ongle', 'massage'], prizes: ['💇 Une coupe ou un soin offert', '💆 Un massage de 60 minutes', '🎁 Un bon beauté de 100 CHF'] },
  { kw: ['fitness', 'sport', 'yoga', 'gym'], prizes: ['🏋️ Un mois d\'abonnement offert', '🤝 Une séance de coaching privée', '🎽 Un pack bienvenue'] },
  { kw: ['fleur', 'boulang', 'boucher', 'épicerie', 'magasin', 'boutique', 'commerce'], prizes: ['🧺 Un panier garni de la maison', '🎁 Un bon d\'achat de 100 CHF', '⭐ Une carte cadeau'] },
];
const DEFAULT_PRIZES = ['🎁 Un bon d\'achat de 100 CHF', '🧺 Un panier garni', '⭐ Une carte cadeau'];

function prizesForSector(sector) {
  const s = (sector || '').toLowerCase();
  for (const grp of PRIZE_SUGGESTIONS) {
    if (grp.kw.some(k => s.includes(k))) return grp.prizes;
  }
  return DEFAULT_PRIZES;
}

const fmtDateFr = (d) => d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' });

// 📡 Canaux d'attribution — chaque support porte son lien ?src=…
const CHANNELS = {
  fb: 'Facebook', ig: 'Instagram', tiktok: 'TikTok',
  'qr-comptoir': 'QR comptoir', 'qr-affiche': 'QR affiche', 'qr-vitrine': 'QR vitrine',
  wa: 'WhatsApp', direct: 'Lien direct / autre',
};

export default function Acquisition() {
  const { companyProfile, tenant, addNotification } = useApp();
  const navigate = useNavigate();
  const cp = companyProfile || {};
  const [pages, setPages] = useState(null);           // toutes les landing concours
  const [mode, setMode] = useState('loading');        // loading | assistant | builder | live
  const [livePage, setLivePage] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [editPage, setEditPage] = useState(null);     // page passée au builder
  const [winnerModal, setWinnerModal] = useState(null); // {winner, emailSent, prize, count} — le moment 🥁
  const [showPv, setShowPv] = useState(false);          // 📄 procès-verbal du tirage
  const [fieldModel, setFieldModel] = useState('relation'); // express | relation | qualifie
  const [objective, setObjective] = useState('concours');   // concours | lead_magnet | promo | event | diagnostic
  const [winnersCount, setWinnersCount] = useState(1);      // 🏆 1-3 gagnants
  const [rewardAll, setRewardAll] = useState(false);        // 🎁 cadeau pour chaque participant
  const [rewardText, setRewardText] = useState('');
  const [redeemCode, setRedeemCode] = useState('');         // 🎟 validation comptoir
  const [brandAssets, setBrandAssets] = useState({ logo: '', photo: '' }); // data-URI pour les visuels sociaux

  // Assistant — 3 réponses
  const [prize, setPrize] = useState('');
  const [customPrize, setCustomPrize] = useState('');
  const [withAddress, setWithAddress] = useState(false);
  const [prizeImage, setPrizeImage] = useState('');   // 📷 photo du lot (URL après upload)
  const [uploadingPrize, setUploadingPrize] = useState(false);
  const [duration, setDuration] = useState('1m');     // '2w' | '1m' | date ISO
  const [customDate, setCustomDate] = useState('');

  const prizeChips = prizesForSector(cp.sector);
  const finalPrize = customPrize.trim() || prize;

  useEffect(() => {
    api.getLandingPages().then(list => {
      const concours = (list || []).filter(p => p.template === 'concours');
      setPages(concours);
      const active = concours.find(p => p.published_at && !p.winner_drawn_at);
      // On atterrit TOUJOURS sur l'accueil du module (explication + menus).
      // Le concours en cours est mémorisé pour être proposé en carte « Suivre ».
      if (active) setLivePage(active);
      setMode('assistant');
    }).catch(() => { setPages([]); setMode('assistant'); });
  }, []);

  useEffect(() => {
    if (livePage?.id) {
      api.getLandingPageStats(livePage.id).then(setLiveStats).catch(() => {});
    }
  }, [livePage?.id]);

  // Prépare logo + photo du lot en data-URI pour les visuels réseaux (téléchargement PNG propre)
  useEffect(() => {
    const logoUrl = livePage?.config?.logo_url || cp.logo_url || '';
    const photoUrl = livePage?.config?.prize_image_url || '';
    let alive = true;
    Promise.all([urlToDataUri(logoUrl), urlToDataUri(photoUrl)]).then(([logo, photo]) => {
      if (alive) setBrandAssets({ logo, photo });
    });
    return () => { alive = false; };
  }, [livePage?.id, livePage?.config?.logo_url, livePage?.config?.prize_image_url, cp.logo_url]);

  const endDateObj = (() => {
    if (duration === '2w') { const d = new Date(); d.setDate(d.getDate() + 14); return d; }
    if (duration === '1m') { const d = new Date(); d.setMonth(d.getMonth() + 1); return d; }
    return customDate ? new Date(customDate) : new Date();
  })();

  const launchBuilder = () => {
    const tpl = getTemplate('concours');
    const dateFr = fmtDateFr(endDateObj);
    const config = {
      ...tpl.defaultConfig,
      headline: `Gagnez ${finalPrize.replace(/^[^\p{L}\p{N}]+\s*/u, '').replace(/^./, c => c.toLowerCase()).replace(/^(un|une|le|la)?\s*/, m => m)} 🎁`.replace('Gagnez ', 'Gagnez '),
      subheadline: 'Un tirage au sort parmi tous les participants — 10 secondes pour jouer',
      prize_name: finalPrize,
      prize_value: '',
      end_text: `Tirage au sort le ${dateFr}`,
      end_date: endDateObj.toISOString().slice(0, 10), // structuré → compte à rebours + relances
      badge_text: 'JEU-CONCOURS',
      cta_text: 'Je tente ma chance 🍀',
      show_name: fieldModel !== 'express',
      show_birthdate: fieldModel !== 'express',
      birthdate_required: fieldModel === 'relation',
      birthdate_year_optional: true,
      show_phone: fieldModel === 'qualifie',
      show_address: fieldModel === 'qualifie' && withAddress,
      show_message: false,
      prize_image_url: prizeImage,
      winners_count: winnersCount,
      reward_all_enabled: rewardAll,
      reward_all_text: rewardAll ? (rewardText.trim() || 'Une petite attention vous attend en magasin — montrez ce code !') : tpl.defaultConfig.reward_all_text,
      accent_color: cp.brand_primary || tpl.defaultConfig.accent_color,
      company_name: tenant?.name || cp.name || 'Mon entreprise',
      logo_url: cp.logo_url || '',
    };
    setEditPage({ template: 'concours', title: `Concours — ${finalPrize.replace(/^[^\p{L}\p{N}]+\s*/u, '')}`, config });
    setMode('builder');
  };

  const OBJECTIVES = [
    { key: 'concours', icon: '🎁', label: 'Faire un concours', title: 'Lancer un jeu-concours', premium: true,
      benefit: 'Les gens s\'inscrivent pour gagner un lot. Chaque participant rejoint ta base, déjà consenti, avec son anniversaire. C\'est le levier le plus rapide et le plus puissant.',
      example: 'Gagnez un nettoyage complet offert' },
    { key: 'lead_magnet', icon: '🧲', label: 'Offre de bienvenue', title: 'Offrir un cadeau de bienvenue',
      benefit: 'Un avantage tout de suite contre un e-mail. Collecte en continu, sans tirage.',
      example: '–10 % sur votre 1ʳᵉ visite' },
    { key: 'promo', icon: '🏷️', label: 'Promo', title: 'Mettre en avant une promo',
      benefit: 'Une offre à durée limitée avec inscription pour en profiter.',
      example: 'Pneus hiver –20 % jusqu\'au 30 nov' },
    { key: 'event', icon: '🎉', label: 'Événement', title: 'Remplir un événement',
      benefit: 'Des inscriptions à ta soirée, ton atelier, tes portes ouvertes.',
      example: 'Portes ouvertes samedi — réservez' },
    { key: 'diagnostic', icon: '🔍', label: 'Demandes de devis', title: 'Recevoir des demandes',
      benefit: 'Un devis ou diagnostic gratuit → des prospects à rappeler.',
      example: 'Devis gratuit en 24 h' },
  ];

  const launchOtherTemplate = () => {
    const tpl = getTemplate(objective);
    const config = {
      ...tpl.defaultConfig,
      accent_color: cp.brand_primary || tpl.defaultConfig.accent_color,
      company_name: tenant?.name || cp.name || 'Mon entreprise',
      logo_url: cp.logo_url || '',
    };
    setEditPage({ template: objective, title: tpl.name + ' — ' + (tenant?.name || ''), config });
    setMode('builder');
  };

  const onPublished = (page) => {
    setPages(ps => [page, ...(ps || []).filter(p => p.id !== page.id)]);
    setLivePage(page);
    setEditPage(null);
    setMode('live');
    addNotification('🧲 Ta page de collecte est en ligne ! Place à l\'amplification.');
  };

  // ── Vue LIVE : amplification ──
  if (mode === 'live' && livePage) {
    const url = `${window.location.origin}/lp/${livePage.slug}`;
    const isDone = !!livePage.winner_drawn_at;
    const daysLeft = (() => {
      const iso = livePage.config?.end_date;
      if (!iso) return null;
      const diff = Math.ceil((new Date(iso) - new Date().setHours(0, 0, 0, 0)) / 86400000);
      return Number.isFinite(diff) ? diff : null;
    })();
    const prizeName = (livePage.config?.prize_name || 'le lot').replace(/^[^\p{L}\p{N}]+\s*/u, '');
    const closedDate = livePage.winner_drawn_at ? new Date(livePage.winner_drawn_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const winnersLeads = (liveStats?.leads || []).filter(l => l.is_winner);
    const winnerLead = winnersLeads[0] || null;
    const nonWinnerEmails = (liveStats?.leads || []).filter(l => !l.is_winner && l.email).map(l => l.email);
    const consolationPreset = {
      startStep: 1,
      preset: {
        name: 'Merci d\'avoir joué 💛',
        objective: 'Fidéliser mes clients',
        channel: 'email',
        tone: 'vouvoiement',
        subject: 'Merci d\'avoir joué, {{prénom}} — une attention vous attend 🎁',
        body: `Bonjour {{prénom}},\n\nMerci d'avoir participé à notre jeu-concours ! Le sort n'a désigné qu'un gagnant… mais on ne voulait pas vous laisser repartir les mains vides.\n\nUne petite attention de consolation vous attend lors de votre prochaine visite — il suffit de mentionner le concours.\n\nAu plaisir de vous voir,\n${tenant?.name || "L'équipe"}\n\nP.S. Promis, on remet ça bientôt — et la prochaine fois, c'est peut-être vous 🍀`,
        filters: { emails: nonWinnerEmails },
      },
    };
    const exportCsv = () => {
      const leads = liveStats?.leads || [];
      if (!leads.length) { addNotification('Aucun participant à exporter pour le moment.', 'error'); return; }
      const head = 'Prénom;Nom;E-mail;Téléphone;Date de naissance;Ville;Participation;Gagnant;Bon;Bon utilisé le;Chances;Canal\n';
      const rows = leads.map(l => [l.first_name, l.last_name, l.email, l.phone, (l.birth_date || '').slice(0, 10), l.city, (l.created_at || '').slice(0, 10), l.is_winner ? 'OUI' : '', l.coupon_code, (l.coupon_redeemed_at || '').slice(0, 10), l.chances || 1, l.source_channel].map(v => (v ?? '')).join(';')).join('\n');
      const blob = new Blob(['\uFEFF' + head + rows], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `participants-${livePage.slug || 'concours'}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    };
    const relancePreset = {
      startStep: 1,
      preset: {
        name: `Dernière ligne droite — concours ⏰`,
        objective: 'Lancer une offre ou une promotion',
        channel: 'email',
        tone: 'vouvoiement',
        subject: `⏰ Plus que ${daysLeft ?? 'quelques'} jour${(daysLeft ?? 2) > 1 ? 's' : ''} pour gagner ${prizeName}, {{prénom}} !`,
        body: `Bonjour {{prénom}},\n\nLe compte à rebours est lancé : notre jeu-concours se termine ${daysLeft === 1 ? 'DEMAIN' : `dans ${daysLeft ?? 'quelques'} jours`} !\n\nÀ gagner : ${prizeName} 🎁\nParticipation en 10 secondes : ${url}\n\nBonne chance 🍀\n${tenant?.name || "L'équipe"}`,
        filters: { exclude_recent: true },
      },
    };
    return (
      <div className="acq">
        <nav className="acq-crumb" aria-label="Fil d'ariane">
          <button type="button" className="acq-crumb-link" onClick={() => setMode('assistant')}>🧲 Collecte</button>
          <span className="acq-crumb-sep">›</span>
          <span className="acq-crumb-cur">{isDone ? 'Concours terminé' : 'Concours en cours'}</span>
        </nav>
        {isDone ? (
          <div className="acq-closed">
            <div className="acq-closed-checker" aria-hidden="true" />
            <div className="acq-closed-ic">🏁</div>
            <div className="acq-closed-txt">
              <div className="acq-closed-title">Concours terminé</div>
              <div className="acq-closed-sub">{prizeName}{closedDate ? <> · clos le <b>{closedDate}</b></> : null} · {winnersLeads.length || 1} gagnant{(winnersLeads.length || 1) > 1 ? 's' : ''} tiré{(winnersLeads.length || 1) > 1 ? 's' : ''}</div>
            </div>
            <div className="acq-closed-spacer" />
            <button className="acq-closed-cta" onClick={() => setMode('assistant')}>+ Nouveau concours</button>
          </div>
        ) : (
          <AcqHero sub="Ta page est en ligne — maintenant, fais-la voir." action={{ label: 'Nouveau concours', onClick: () => setMode('assistant') }} />
        )}
        {isDone ? (
          <div className="acq-bilan">
            <div className="acq-bilan-cell"><span className="acq-bilan-n">{liveStats?.visits_count ?? 0}</span><span className="acq-bilan-l">visite{(liveStats?.visits_count ?? 0) > 1 ? 's' : ''}</span></div>
            <div className="acq-bilan-cell"><span className="acq-bilan-n">{liveStats?.leads_count ?? 0}</span><span className="acq-bilan-l">participant{(liveStats?.leads_count ?? 0) > 1 ? 's' : ''}</span></div>
            <div className="acq-bilan-cell acq-bilan-cell--accent"><span className="acq-bilan-n">+{liveStats?.leads_count ?? 0}</span><span className="acq-bilan-l">contact{(liveStats?.leads_count ?? 0) > 1 ? 's' : ''} gagné{(liveStats?.leads_count ?? 0) > 1 ? 's' : ''}<br />consenti · anniversaire</span></div>
            <button type="button" className="acq-bilan-cell acq-bilan-report" onClick={() => document.getElementById('acq-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              <span className="acq-bilan-ri">📊</span>
              <span className="acq-bilan-l"><b>Rapport complet</b><br />courbe · sources · projection</span>
              <span className="acq-bilan-chev">→</span>
            </button>
          </div>
        ) : (
          <AcqKpis pages={pages} />
        )}
        <div className={`acq-live-top acq-card${isDone ? ' acq-live-top--done' : ''}`}>
          {!isDone && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <img className="acq-qr" alt="QR comptoir" src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(url + '?src=qr-comptoir')}`} />
            <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>QR comptoir (tracé)</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['qr-affiche', '🖼 Affiche'], ['qr-vitrine', '🪟 Vitrine']].map(([k, lbl]) => (
                <a key={k} className="acq-btn" style={{ padding: '5px 10px', fontSize: 11 }} target="_blank" rel="noreferrer"
                  href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(url + '?src=' + k)}`}
                  title={`Ouvrir le QR ${lbl} en grand (clic droit → enregistrer)`}>{lbl}</a>
              ))}
              <button className="acq-btn" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => {
                const w = window.open('', '_blank');
                w.document.write(`<!DOCTYPE html><html><head><title>Affiche concours</title></head>
<body style="margin:0;font-family:Arial;display:flex;align-items:center;justify-content:center;min-height:100vh">
<div style="text-align:center;padding:40px;max-width:600px">
${livePage.config?.logo_url ? `<img src="${livePage.config.logo_url}" style="height:70px;margin-bottom:24px"/>` : ''}
<div style="font-size:44px;font-weight:900;line-height:1.2;margin-bottom:10px">${(livePage.config?.headline || 'Grand jeu-concours').replace(/</g, '&lt;')}</div>
<div style="font-size:24px;color:#444;margin-bottom:30px">${(livePage.config?.prize_name || '').replace(/</g, '&lt;')}</div>
<img src="https://api.qrserver.com/v1/create-qr-code/?size=340x340&margin=10&data=${encodeURIComponent(url + '?src=qr-affiche')}" style="border:6px solid #111;border-radius:20px"/>
<div style="font-size:22px;font-weight:800;margin-top:26px">📱 Scannez, participez — 10 secondes chrono !</div>
<div style="font-size:15px;color:#666;margin-top:10px">${(livePage.config?.end_text || '').replace(/</g, '&lt;')} · Gratuit et sans obligation d'achat</div>
</div></body></html>`);
                w.document.close();
                setTimeout(() => w.print(), 600);
              }}>🖨 Affiche A4</button>
            </div>
          </div>
          )}
          <div className="acq-live-info">
            {!isDone && (
              <div className="acq-url" onClick={() => { navigator.clipboard?.writeText(url); addNotification('Lien copié 📋'); }} title="Cliquer pour copier">
                🔗 {url}
              </div>
            )}
            {!isDone && daysLeft != null && (
              <div className="acq-live-stats">
                <span>👀 <b>{liveStats?.visits_count ?? 0}</b> visites</span>
                <span>🎂 <b>{liveStats?.leads_count ?? 0}</b> participants</span>
                <span className={`acq-days ${daysLeft <= 7 ? 'hot' : ''}`}>
                  {daysLeft > 1 ? `⏳ Plus que ${daysLeft} jours` : daysLeft === 1 ? '⚡ Dernier jour demain !' : daysLeft === 0 ? '🥁 Tirage aujourd\'hui !' : '📅 Tirage à faire'}
                </span>
              </div>
            )}
            {isDone && winnerLead && (() => {
              const wf = livePage.config?.winner_workflow || {};
              const deadline = wf.deadline ? new Date(wf.deadline) : null;
              const hoursLeft = deadline ? Math.max(0, Math.round((deadline - Date.now()) / 3600000)) : null;
              return (
                <div className="acq-won-card">
                  <div className="acq-winner-band">
                    <div className="acq-winner-medal" aria-hidden="true">🏆</div>
                    <div className="acq-winner-idbox">
                      <span className="acq-winner-kicker">Le gagnant{winnersLeads.length > 1 ? 's' : ''}</span>
                      <span className="acq-winner-name">{winnersLeads.map(w => [w.first_name, w.last_name].filter(Boolean).join(' ') || w.email).join(' · ')}</span>
                      <span className="acq-winner-contact"><b>{winnerLead.email}</b>{winnerLead.phone ? ` · ${winnerLead.phone}` : ''} <span className="acq-winner-remise">— pour la remise du lot</span></span>
                    </div>
                    <span className={`acq-winner-status ${wf.status === 'confirmed' ? 'ok' : 'wait'}`}>
                      {wf.status === 'confirmed'
                        ? '✅ Gagnant confirmé'
                        : `⏱ ${hoursLeft != null ? (hoursLeft > 0 ? `${hoursLeft}h pour répondre` : 'délai 72 h dépassé') : 'en attente'}`}
                    </span>
                  </div>
                  <div className="acq-winner-foot">
                    {wf.status !== 'confirmed' && (
                      <>
                        <button className="acq-btn" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={async () => {
                          try { await api.landingWinnerAction(livePage.id, 'confirm'); setLivePage(pg => ({ ...pg, config: { ...pg.config, winner_workflow: { ...(pg.config?.winner_workflow || {}), status: 'confirmed' } } })); addNotification('✅ Gagnant confirmé — pense à la remise du lot.'); }
                          catch (e) { addNotification(e.message || 'Action impossible.', 'error'); }
                        }}>✅ Il a répondu</button>
                        <button className="acq-btn" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={async () => {
                          if (!window.confirm('Passer au suppléant ? Le gagnant actuel perd le lot, le suppléant est désigné et reçoit son e-mail automatiquement. Décision journalisée au procès-verbal.')) return;
                          try {
                            const r = await api.landingWinnerAction(livePage.id, 'replace');
                            setLiveStats(null);
                            api.getLandingPageStats(livePage.id).then(setLiveStats).catch(() => {});
                            api.getLandingPages().then(list => { const pg = (list || []).find(x => x.id === livePage.id); if (pg) setLivePage(pg); }).catch(() => {});
                            addNotification(r?.winner ? `🥁 Suppléant désigné : ${r.winner.first_name || r.winner.email}${r.winner_email_sent ? ' — e-mail envoyé' : ''}` : 'Suppléant désigné.');
                          } catch (e) { addNotification(e.message || 'Plus de suppléant disponible.', 'error'); }
                        }}>😶 Injoignable → suppléant</button>
                      </>
                    )}
                    {livePage.config?.draw && (
                      <button className="acq-btn" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={() => setShowPv(true)}>📄 Procès-verbal</button>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="acq-live-actions">
              <a className="acq-btn acq-btn--primary" href={url} target="_blank" rel="noreferrer">Voir ma page →</a>
              {isDone && (
                <>
                  <button className="acq-btn" onClick={() => setWinnerModal({ winner: winnerLead || {}, emailSent: null, prize: prizeName, count: liveStats?.leads_count ?? 0, names: [], animate: false })} disabled={!winnerLead}>
                    🏆 Revoir le gagnant
                  </button>
                  {nonWinnerEmails.length > 0 && (
                    <button className="acq-btn" onClick={() => navigate('/campaigns/new', { state: consolationPreset })} title="Campagne déjà écrite vers tous les participants non gagnants">
                      💛 Relancer les {nonWinnerEmails.length} participants
                    </button>
                  )}
                  <button className="acq-btn" onClick={async () => {
                    try {
                      await api.unpublishLandingPage(livePage.id);
                      setLivePage(null); setLiveStats(null); setMode('assistant');
                      addNotification('📁 Concours archivé — il reste dans ton historique avec tous ses participants.');
                    } catch (e) { addNotification(e.message || 'Archivage impossible.', 'error'); }
                  }}>📁 Archiver</button>
                </>
              )}
              {!isDone && (
                <button className="acq-btn" onClick={() => { setEditPage(livePage); setMode('builder'); }}>✏️ Modifier</button>
              )}
              <button className="acq-btn" onClick={exportCsv} title="Télécharger la liste des participants (CSV pour Excel)">
                ⬇️ Exporter (CSV)
              </button>
              {!isDone && <button className="acq-btn" onClick={async () => {
                if (!window.confirm('Dépublier cette page ? Les participants déjà inscrits restent dans ta base — la page ne sera plus accessible.')) return;
                try {
                  await api.unpublishLandingPage(livePage.id);
                  setLivePage(null); setLiveStats(null); setMode('assistant');
                  addNotification('Page dépubliée — les participants et l\'historique sont conservés.');
                } catch (e) { addNotification(e.message || 'Impossible de dépublier.', 'error'); }
              }} title="Retirer la page du web (historique conservé)">
                🗑 Dépublier
              </button>}
              {!isDone && <button className="acq-btn" onClick={async () => {
                try {
                  const r = await api.drawLandingPageWinner(livePage.id);
                  setLivePage(pg => ({ ...pg, winner_drawn_at: new Date().toISOString() }));
                  if (r?.winner) {
                    setWinnerModal({
                      winner: r.winner,
                      emailSent: !!r.winner_email_sent,
                      prize: (livePage.config?.prize_name || 'le lot').replace(/^[^\p{L}\p{N}]+\s*/u, ''),
                      count: liveStats?.leads_count ?? 0,
                      names: (liveStats?.leads || []).map(l => l.first_name || (l.name || '').split(' ')[0]).filter(Boolean),
                      animate: true,
                    });
                  } else {
                    addNotification(r?.message || 'Tirage effectué.');
                  }
                } catch (e) { addNotification(e.message || 'Tirage impossible pour le moment.', 'error'); }
              }}>🥁 Tirer au sort le gagnant</button>}
            </div>
            <div className="acq-live-note">🛡️ Chaque participant entre dans ta base <b>déjà consenti</b> (horodaté nLPD), avec son anniversaire — les vœux automatiques prennent le relais.</div>
            {livePage.config?.reward_all_enabled && (() => {
              const emitted = (liveStats?.leads || []).filter(l => l.coupon_code).length;
              const used = (liveStats?.leads || []).filter(l => l.coupon_redeemed_at).length;
              return (
                <div style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A', borderRadius: 12, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#78350F' }}>🎟 Bons : {used}/{emitted} utilisés</span>
                  <span style={{ fontSize: 11.5, color: '#92400E' }}>= visites mesurées</span>
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
                    <input value={redeemCode} onChange={e => setRedeemCode(e.target.value.toUpperCase())} placeholder="Code du client…"
                      style={{ border: '1.5px solid #FDE68A', borderRadius: 999, padding: '7px 13px', fontSize: 12.5, fontFamily: 'inherit', width: 130, textTransform: 'uppercase' }} />
                    <button className="acq-btn" style={{ borderColor: '#F59E0B', color: '#92400E' }} onClick={async () => {
                      if (!redeemCode.trim()) return;
                      try {
                        const r = await api.redeemCoupon(livePage.id, redeemCode.trim());
                        addNotification(`✅ Bon validé — bonne visite à ${r.name} !`);
                        setRedeemCode('');
                        api.getLandingPageStats(livePage.id).then(setLiveStats).catch(() => {});
                      } catch (e) { addNotification(e.message || 'Code introuvable.', 'error'); }
                    }}>Valider</button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div id="acq-report"><ConcoursReport page={livePage} stats={liveStats} daysLeft={daysLeft} isDone={isDone} /></div>

        {!isDone && daysLeft != null && daysLeft <= 7 && daysLeft >= 0 && (
          <div className="acq-relance">
            <span className="acq-relance-ic">⏰</span>
            <div className="acq-relance-txt">
              <b>{daysLeft <= 1 ? 'C\'est la toute dernière chance' : `Plus que ${daysLeft} jours`} — le moment parfait pour relancer.</b>
              <span>Une campagne « dernière ligne droite » est déjà écrite : sujet, message, lien du concours, anti-fatigue. Tu relis, tu envoies.</span>
            </div>
            <button className="acq-btn acq-btn--primary" onClick={() => navigate('/campaigns/new', { state: relancePreset })}>
              🚀 Lancer la relance →
            </button>
          </div>
        )}

        {!isDone && (<>
        <h2 className="acq-h2">📣 Fais-la voir — tes visuels sont prêts</h2>
        <p className="acq-sub">Un visuel par moment, à ton image. Tu télécharges, tu postes. Les vidéos ont leur mini-script.</p>
        {(() => {
          const ctx = {
            company: tenant?.name || 'Mon commerce',
            logoDataUri: brandAssets.logo,
            photoDataUri: brandAssets.photo,
            endDate: (livePage.config?.end_text || '').replace(/^Tirage au sort le /i, '') || 'bientôt',
          };
          const socialPlan = buildSocialPlan({ prize: livePage.config?.prize_name || 'le lot', company: ctx.company, endDate: ctx.endDate });
          const shown = isDone ? socialPlan.filter(m => m.key === 'fin') : socialPlan;
          return (
            <>
              {/* ⏱ Timeline flagrante des 3 moments */}
              <div className="acq-tl">
                {shown.map((m, i) => (
                  <React.Fragment key={m.key}>
                    {i > 0 && <div className="acq-tl-line" />}
                    <div className="acq-tl-step" style={{ '--tl': m.dot }}>
                      <div className="acq-tl-node">{m.icon}</div>
                      <div className="acq-tl-title">{m.title}</div>
                      <div className="acq-tl-when">{m.when}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {shown.map((moment, mi) => (
            <div key={moment.key} className="acq-sv-moment-wrap">
              <div className="acq-sv-moment" style={{ '--tl': moment.dot }}>
                <span className="acq-sv-moment-badge" style={{ background: moment.dot }}>{mi + 1}</span>
                <span className="acq-sv-moment-title">{moment.icon} {moment.title}</span>
                <span className="acq-sv-when">📅 {moment.when}</span>
              </div>
              <div className="acq-sv-gal">
                {moment.items.map(it => {
                  const svg = buildVisualSVG(it, ctx);
                  return (
                    <div key={it.id} className="acq-sv-card">
                      {it.postWhen && (
                        <div className="acq-sv-ribbon" style={{ '--tl': moment.dot }}>
                          <span className="acq-sv-ribbon-day">📅 {it.postWhen.day}</span>
                          <span className="acq-sv-ribbon-note">{it.postWhen.note}</span>
                        </div>
                      )}
                      <div className={`acq-sv-visual acq-sv-${it.format}`}>
                        <img src={svgToDataUri(svg)} alt={it.doLabel} loading="lazy" />
                      </div>
                      <div className="acq-sv-cap">
                        <div className="acq-sv-do">{it.doLabel}<span className={`acq-sv-tag ${it.doTag.kind}`}>{it.doTag.label}</span></div>
                        {it.why && <div className="acq-sv-why">💡 {it.why}</div>}
                        <div className="acq-sv-txt">{it.caption}</div>
                        <div className="acq-sv-act">
                          {it.isPoster ? (
                            <button className="acq-btn acq-btn--primary acq-sv-grow" onClick={() => document.querySelector('.acq-live-actions')?.scrollIntoView({ behavior: 'smooth' }) || addNotification('Utilise le bouton 🖨 Affiche A4 en haut de la page.')}>🖨 Affiche A4</button>
                          ) : it.isVideo ? (
                            <button className="acq-btn acq-sv-grow" onClick={() => { navigator.clipboard?.writeText(it.caption); addNotification('Script copié 📋 — à toi de filmer !'); }}>📋 Copier le script</button>
                          ) : (
                            <>
                              <button className="acq-btn acq-btn--primary acq-sv-grow" onClick={() => downloadSvgAsPng(svg, `${it.id}.png`, () => { const w = window.open(); if (w) { w.document.write(`<img src="${svgToDataUri(svg)}" style="max-width:100%"/>`); } addNotification('Ouvre l\'image et fais « enregistrer ».', 'error'); })}>⬇️ Télécharger</button>
                              <button className="acq-btn" title="Copier la légende" onClick={() => { navigator.clipboard?.writeText(it.caption); addNotification('Légende copiée 📋'); }}>📋</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
              ))}
            </>
          );
        })()}
        </>)}

        {showPv && livePage.config?.draw && (
          <PvModal draw={livePage.config.draw} page={livePage} onClose={() => setShowPv(false)} />
        )}
        <ConcoursHistory pages={pages} currentId={livePage.id} onOpen={(p) => { setLivePage(p); setLiveStats(null); }} />
        {winnerModal && <WinnerModal data={winnerModal} company={tenant?.name || 'notre commerce'} onClose={() => setWinnerModal(null)} addNotification={addNotification} />}
      </div>
    );
  }

  // ── Vue BUILDER (création prête à modifier, ou édition d'une page existante) ──
  if (mode === 'builder' && editPage) {
    return (
      <div className="acq">
        <nav className="acq-crumb" aria-label="Fil d'ariane">
          <button type="button" className="acq-crumb-link" onClick={() => setMode(livePage ? 'live' : 'assistant')}>🧲 Collecte</button>
          <span className="acq-crumb-sep">›</span>
          <span className="acq-crumb-cur">{editPage?.id ? 'Modifier la page' : 'Nouvelle page'}</span>
        </nav>
        <AcqHero sub="Relis, ajuste si tu veux — puis publie." />
        <LandingBuilder
          existingPage={editPage.id ? editPage : editPage /* préconfig sans id → création */}
          onClose={() => setMode(livePage ? 'live' : 'assistant')}
          onPublished={onPublished}
        />
      </div>
    );
  }

  // ── Vue ASSISTANT : 3 questions ──
  return (
    <div className="acq">
      <AcqHero sub="3 questions, et ta page de collecte est prête — participants = contacts consentis, anniversaires compris." />
      <AcqKpis pages={pages} />
      {mode === 'loading' ? (
        <div className="acq-card" style={{ textAlign: 'center', padding: 40 }}>🧞 Un instant…</div>
      ) : (
        <>
          <div className="acq-role">
            <span className="acq-role-ic">🌱</span>
            <div className="acq-role-txt">
              <span className="acq-role-tag">Ici : GRANDIR</span> ce module fait entrer de <b>nouveaux</b> contacts.
              <br /><b>Animer tes clients actuels</b> (vœux d'anniversaire, relances) tourne déjà <b>tout seul, en fond</b> — rien à faire ici pour ça.
            </div>
          </div>

          {livePage && livePage.published_at && !livePage.winner_drawn_at && (
            <button type="button" className="acq-live-strip" onClick={() => setMode('live')}>
              <span className="acq-live-strip-ic">🧲</span>
              <span className="acq-live-strip-txt">
                <b>Concours en cours — {(livePage.config?.prize_name || livePage.title || '').replace(/^[^\p{L}\p{N}]+\s*/u, '')}</b>
                <span>{liveStats
                  ? `👀 ${liveStats.visits_count ?? 0} visites · 🎂 ${liveStats.leads_count ?? 0} participant${(liveStats.leads_count ?? 0) > 1 ? 's' : ''}`
                  : 'Ton concours tourne — clique pour le suivre et l\'amplifier'}</span>
              </span>
              <span className="acq-live-strip-cta">Suivre &amp; amplifier →</span>
            </button>
          )}

          <div className="acq-card">
            <div className="acq-obj-q">🎯 Que veux-tu accomplir ?</div>
            <p className="acq-obj-sub">Un chemin recommandé, et des alternatives claires. Choisis, le reste se prépare tout seul.</p>

            {(() => { const reco = OBJECTIVES[0]; const others = OBJECTIVES.slice(1); return (
              <>
                <button type="button" className={`acq-reco ${objective === 'concours' ? 'on' : ''}`} onClick={() => setObjective('concours')}>
                  <span className="acq-reco-badge">⭐ Recommandé — collecte le plus</span>
                  <span className="acq-reco-ic">{reco.icon}</span>
                  <span className="acq-reco-body">
                    <span className="acq-reco-h">{reco.title}<span className="acq-reco-paid">action premium</span></span>
                    <span className="acq-reco-p">{reco.benefit}</span>
                    <span className="acq-reco-ex">💡 Exemple : « {reco.example} »</span>
                  </span>
                  <span className="acq-reco-check">{objective === 'concours' ? '✓' : ''}</span>
                </button>

                <div className="acq-obj-or">ou un autre levier — gratuit</div>
                <div className="acq-obj-others">
                  {others.map(o => (
                    <button type="button" key={o.key} className={`acq-opt ${objective === o.key ? 'on' : ''}`} onClick={() => setObjective(o.key)}>
                      <span className="acq-opt-ic">{o.icon}</span>
                      <span className="acq-opt-body">
                        <span className="acq-opt-h">{o.title}</span>
                        <span className="acq-opt-p">{o.benefit}</span>
                        <span className="acq-opt-ex">ex. « {o.example} »</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ); })()}
          </div>

          {objective !== 'concours' && (
            <div className="acq-cta-row">
              <button className="acq-big-cta" onClick={launchOtherTemplate}>✨ Créer ma page « {OBJECTIVES.find(o => o.key === objective)?.title} » →</button>
              <p className="acq-hint" style={{ marginTop: 8 }}>Page pré-remplie à tes couleurs — tu ajustes les textes et tu publies (gratuit, hors concours).</p>
            </div>
          )}

          {objective === 'concours' && (
          <>
          <div className="acq-assist">
            <div className="acq-q">
              <span className="acq-q-num">1</span>
              <h3>Qu'est-ce qu'on fait gagner ?</h3>
              <div className="acq-chips">
                {prizeChips.map(p => (
                  <button key={p} className={`acq-chip ${prize === p && !customPrize ? 'on' : ''}`} onClick={() => { setPrize(p); setCustomPrize(''); }}>{p}</button>
                ))}
              </div>
              <input className="acq-input" placeholder="✍️ Ou écris ton lot…" value={customPrize} onChange={e => setCustomPrize(e.target.value)} />
              <label className={`acq-prize-photo${prizeImage ? ' has' : ''}${uploadingPrize ? ' loading' : ''}`}>
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingPrize}
                  onChange={async e => {
                    const f = e.target.files[0]; e.target.value = '';
                    if (!f) return;
                    if (f.size > 5 * 1024 * 1024) { addNotification('Image trop lourde (max 5 Mo).', 'error'); return; }
                    setUploadingPrize(true);
                    try {
                      const fd = new FormData(); fd.append('image', f);
                      const r = await api.uploadCampaignImage(fd);
                      if (r?.url) { setPrizeImage(r.url); addNotification('📷 Photo du lot ajoutée !'); }
                      else addNotification('Upload impossible.', 'error');
                    } catch (err) { addNotification(err.message || 'Upload impossible.', 'error'); }
                    finally { setUploadingPrize(false); }
                  }} />
                {prizeImage ? (
                  <>
                    <img src={prizeImage} alt="lot" className="acq-prize-photo-thumb" />
                    <span className="acq-prize-photo-body">
                      <span className="acq-prize-photo-title">✅ Photo du lot ajoutée</span>
                      <span className="acq-prize-photo-sub">{uploadingPrize ? 'Envoi…' : 'Clique pour la remplacer'}</span>
                    </span>
                    <span className="acq-prize-photo-cta">Changer</span>
                  </>
                ) : (
                  <>
                    <span className="acq-prize-photo-ic">📷</span>
                    <span className="acq-prize-photo-body">
                      <span className="acq-prize-photo-title">{uploadingPrize ? '⏳ Envoi de la photo…' : 'Ajoute une photo du lot'}</span>
                      <span className="acq-prize-photo-sub">C'est le <b>1ᵉʳ facteur de participation</b> — une belle photo peut doubler les inscriptions.</span>
                    </span>
                    <span className="acq-prize-photo-cta">Parcourir →</span>
                  </>
                )}
              </label>
              <p className="acq-hint">Le lot fait la page : il apparaît en héros, aux couleurs de ta marque.</p>
            </div>
            <div className="acq-q">
              <span className="acq-q-num">2</span>
              <h3>Quelles infos récolter ?</h3>
              <div className="acq-chips" style={{ marginBottom: 8 }}>
                {[['express', '⚡ Express', 'prénom + e-mail'], ['relation', '💛 Relation', '+ jour/mois d\'anniversaire'], ['qualifie', '🎯 Qualifié', '+ téléphone + NPA']].map(([k, lbl]) => (
                  <button key={k} className={`acq-chip ${fieldModel === k ? 'on' : ''}`} onClick={() => setFieldModel(k)}>{lbl}</button>
                ))}
              </div>
              <p className="acq-hint" style={{ margin: '0 0 8px' }}>
                {fieldModel === 'express' && <>⚡ <b>Prénom + e-mail.</b> Le formulaire le plus court = le maximum de participants. Idéal pour une première collecte.</>}
                {fieldModel === 'relation' && <>💛 <b>Prénom, nom, e-mail + jour/mois de naissance</b> (année facultative). Le bon équilibre : les vœux d'anniversaire s'activent, sans faire fuir personne. <b>Recommandé.</b></>}
                {fieldModel === 'qualifie' && <>🎯 <b>+ téléphone et NPA.</b> Des contacts très complets, mais compte 10-20% de participants en moins — le génie te préviendra si ça freine.</>}
              </p>
              {fieldModel === 'qualifie' && (
                <div className="acq-chips">
                  <button className={`acq-chip ${withAddress ? 'on' : ''}`} onClick={() => setWithAddress(v => !v)}>Adresse complète {withAddress ? '✓' : ''}</button>
                </div>
              )}
            </div>
            <div className="acq-q">
              <span className="acq-q-num">3</span>
              <h3>Jusqu'à quand ?</h3>
              <div className="acq-chips">
                <button className={`acq-chip ${duration === '2w' ? 'on' : ''}`} onClick={() => setDuration('2w')}>2 semaines</button>
                <button className={`acq-chip ${duration === '1m' ? 'on' : ''}`} onClick={() => setDuration('1m')}>1 mois</button>
                <button className={`acq-chip ${duration === 'custom' ? 'on' : ''}`} onClick={() => setDuration('custom')}>Date précise</button>
              </div>
              {duration === 'custom' && (
                <input type="date" className="acq-input" value={customDate} onChange={e => setCustomDate(e.target.value)} />
              )}
              <p className="acq-hint">Donne le compte à rebours de la page — et la date du tirage ({fmtDateFr(endDateObj)}).</p>
              <div style={{ borderTop: '1px solid var(--line)', marginTop: 12, paddingTop: 10 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>🏆 Combien de gagnants ?</div>
                <div className="acq-chips">
                  {[1, 2, 3].map(n => (
                    <button key={n} className={`acq-chip ${winnersCount === n ? 'on' : ''}`} onClick={() => setWinnersCount(n)}>{n}</button>
                  ))}
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, cursor: 'pointer', fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={rewardAll} onChange={e => setRewardAll(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--violet)' }} />
                  <span><b>🎁 + un cadeau pour CHAQUE participant</b> — chacun reçoit un bon à code unique à venir utiliser en magasin. C'est ce qui transforme le concours en visites réelles.</span>
                </label>
                {rewardAll && (
                  <input className="acq-input" placeholder="Ex : Un café offert · 10% sur ton prochain passage…" value={rewardText} onChange={e => setRewardText(e.target.value)} />
                )}
              </div>
            </div>
          </div>
          {tenant?.plan !== 'premium' && !((tenant?.settings?.concours_credits ?? 0) > 0) && (
            <div className="acq-unlock">
              <span className="acq-unlock-ic">💎</span>
              <div className="acq-unlock-txt">
                <b>Le jeu-concours complet est inclus dans Premium.</b>
                <span>Pas envie de changer de plan pour un seul concours ? Débloque celui-ci en one-shot — jeu complet (page, tirage, e-mail au gagnant) et historique conservé pour toujours.</span>
              </div>
              <div className="acq-unlock-btns">
                <button className="acq-btn acq-btn--primary" onClick={async () => {
                  try { const r = await api.checkoutPack('concours', 0, '/collecte'); if (r?.url) window.location.href = r.url; }
                  catch (e) { addNotification(e.message || 'Paiement indisponible.', 'error'); }
                }}>💳 Débloquer ce concours — 9 CHF</button>
                <button className="acq-btn" onClick={() => navigate('/settings?tab=plan')}>⭐ Voir Premium</button>
              </div>
            </div>
          )}
          <div className="acq-cta-row">
            <button className="acq-big-cta" disabled={!finalPrize || (duration === 'custom' && !customDate)} onClick={launchBuilder}>
              ✨ Créer ma page de collecte →
            </button>
          </div>
          </>
          )}
          <ConcoursHistory pages={pages} currentId={null} onOpen={(p) => { setLivePage(p); setLiveStats(null); setMode('live'); }} />
        </>
      )}
    </div>
  );
}

function AcqHero({ sub, action }) {
  return (
    <div className="acq-hero">
      <div className="acq-hero-nebula" />
      <div className="acq-hero-inner" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="acq-hero-icon">🧲</div>
          <div>
            <h1 className="acq-hero-title">Fais grandir ta base</h1>
            <p className="acq-hero-sub">{sub}</p>
          </div>
        </div>
        {action && (
          <button type="button" onClick={action.onClick} className="acq-hero-cta">
            + {action.label}
          </button>
        )}
      </div>
    </div>
  );
}


/* ── 📁 Historique des concours — visible partout, statuts clairs ─────────── */
function ConcoursHistory({ pages, currentId, onOpen }) {
  const [details, setDetails] = useState({}); // id → {leads_count, winnerName}

  const list = (pages || []).filter(p => p.id && p.id !== currentId);

  useEffect(() => {
    list.forEach(p => {
      if (details[p.id] !== undefined) return;
      api.getLandingPageStats(p.id).then(st => {
        const winner = (st?.leads || []).find(l => l.is_winner);
        setDetails(d => ({ ...d, [p.id]: { leads: st?.leads_count ?? 0, winner: winner ? `${winner.first_name || ''} ${winner.last_name || ''}`.trim() || winner.email : null } }));
      }).catch(() => setDetails(d => ({ ...d, [p.id]: { leads: null, winner: null } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, currentId]);

  if (!list.length) return null;
  const withLeads = list.map(p => ({ p, n: details[p.id]?.leads })).filter(x => x.n != null);
  const best = withLeads.length >= 2 ? withLeads.reduce((a, b) => (b.n > a.n ? b : a)) : null;
  return (
    <div className="acq-card">
      <div style={{ fontWeight: 900, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>📁 Historique de tes concours</div>
      {best && best.n > 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
          🏅 Ton record : <b style={{ color: 'var(--ink)' }}>{best.n} participant{best.n > 1 ? 's' : ''}</b> avec « {best.p.config?.prize_name || best.p.title} » — un bon lot fait toute la différence.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map(p => {
          const d = details[p.id] || {};
          const status = p.winner_drawn_at ? { lbl: '🏁 Terminé', cls: 'done' } : p.published_at ? { lbl: '🟢 En ligne', cls: 'live' } : { lbl: '✏️ Brouillon', cls: 'draft' };
          return (
            <div key={p.id} className="acq-hist-row">
              <div style={{ flex: 1, minWidth: 200 }}>
                <b style={{ fontSize: 13, color: 'var(--ink)' }}>{p.config?.prize_name || p.title}</b>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                  {p.config?.end_text || ''}{d.leads != null ? ` · 🎂 ${d.leads} participant${d.leads > 1 ? 's' : ''}` : ''}{d.winner ? ` · 🏆 ${d.winner}` : ''}
                </span>
              </div>
              <span className={`acq-hist-status ${status.cls}`}>{status.lbl}</span>
              <button className="acq-btn" onClick={() => onOpen(p)}>Consulter →</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── 🥁 Le moment du tirage — annonce wow + prochaines étapes ─────────────── */
function fireConfetti(canvas) {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.width = window.innerWidth * dpr;
  const H = canvas.height = window.innerHeight * dpr;
  canvas.style.width = window.innerWidth + 'px';
  canvas.style.height = window.innerHeight + 'px';
  const ctx = canvas.getContext('2d');
  const colors = ['#6C5CE7', '#F5A623', '#EC4899', '#10B981', '#2563EB', '#FFC400'];
  const cx = W / 2, cy = H * 0.34;
  const parts = Array.from({ length: 150 }, () => {
    const a = Math.random() * Math.PI * 2, sp = (4 + Math.random() * 9) * dpr;
    return { x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 6 * dpr,
      w: (6 + Math.random() * 7) * dpr, h: (9 + Math.random() * 8) * dpr,
      rot: Math.random() * Math.PI, vr: (Math.random() - .5) * .3, c: colors[(Math.random() * colors.length) | 0], life: 0 };
  });
  const start = performance.now();
  (function frame(t) {
    const el = t - start;
    ctx.clearRect(0, 0, W, H);
    parts.forEach(p => {
      p.vy += 0.28 * dpr; p.x += p.vx; p.y += p.vy; p.vx *= 0.99; p.rot += p.vr; p.life = el;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, 1 - el / 2600);
      ctx.fillStyle = p.c; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
    });
    if (el < 2600) requestAnimationFrame(frame);
    else ctx.clearRect(0, 0, W, H);
  })(start);
}

function WinnerModal({ data, company, onClose, addNotification }) {
  const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const winnerName = `${data.winner.first_name || ''} ${data.winner.last_name || ''}`.trim() || data.winner.email || 'Notre gagnant';
  const pool = (data.names && data.names.length ? data.names : [winnerName]);
  const [phase, setPhase] = React.useState(data.animate && !reduce ? 'drawing' : 'revealed');
  const [spin, setSpin] = React.useState(pool[0] || '…');
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (phase !== 'drawing') return;
    let i = 0;
    const iv = setInterval(() => { i++; setSpin(pool[i % pool.length]); }, 85);
    const to = setTimeout(() => { clearInterval(iv); setPhase('revealed'); }, 2600);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [phase]); // eslint-disable-line

  React.useEffect(() => {
    if (phase === 'revealed' && !reduce) { const t = setTimeout(() => fireConfetti(canvasRef.current), 60); return () => clearTimeout(t); }
  }, [phase, reduce]);

  const announce = `🥁 Roulement de tambour… Bravo à ${data.winner.first_name || 'notre client'} qui remporte ${data.prize} chez ${company} ! 🎉\n\nMerci aux ${data.count} participant${data.count > 1 ? 's' : ''} — on remet ça très vite, restez connectés 😉`;
  return createPortal(
    <div className="acq-winner-backdrop" onClick={phase === 'revealed' ? onClose : undefined}>
      <canvas ref={canvasRef} className="acq-confetti" aria-hidden="true" />
      {phase === 'drawing' ? (
        <div className="acq-winner-card acq-winner-card--draw" onClick={e => e.stopPropagation()}>
          <div className="acq-winner-drum acq-drum-shake">🥁</div>
          <div className="acq-winner-eyebrow">Tirage au sort en cours…</div>
          <div className="acq-winner-spin">{spin}</div>
          <div className="acq-winner-dots"><span /><span /><span /></div>
        </div>
      ) : (
      <div className="acq-winner-card" onClick={e => e.stopPropagation()}>
        <div className="acq-winner-trophy">🏆</div>
        <div className="acq-winner-eyebrow">Le sort a parlé…</div>
        <h2 className="acq-winner-name">{data.winner.first_name || ''} {data.winner.last_name || ''}</h2>
        <div className="acq-winner-prize">remporte {data.prize} 🎁</div>
        <div className="acq-winner-steps">
          <div className={`acq-winner-step ${data.emailSent !== false ? 'ok' : ''}`}>
            {data.emailSent === false
              ? <>📞 Contacte le gagnant ({data.winner.email}{data.winner.phone ? ` · ${data.winner.phone}` : ''}) pour la remise du lot</>
              : <>✅ E-mail de félicitations envoyé avec les infos de retrait · {data.winner.email}{data.winner.phone ? ` · ${data.winner.phone}` : ''}</>}
          </div>
          <div className="acq-winner-step">🏁 Le concours est fermé — la page publique affiche « terminé », plus aucune participation possible</div>
          <div className="acq-winner-step">📣 Dernier coup de projecteur : annonce le gagnant sur tes réseaux (le contenu le plus partagé !)</div>
        </div>
        <div className="acq-winner-actions">
          <button className="acq-btn acq-btn--primary" onClick={() => { navigator.clipboard?.writeText(announce); addNotification('Post d\'annonce copié 📋 — colle-le sur tes réseaux'); }}>
            📣 Copier le post d'annonce
          </button>
          <button className="acq-btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
      )}
    </div>,
    document.body
  );
}

/* ── 📊 Rapport live du concours — stats, courbe par jour, conseils du génie ── */
function ConcoursReport({ page, stats, daysLeft, isDone }) {
  if (!stats) return null;
  const visits = stats.visits_count ?? 0;
  const leads = stats.leads_count ?? 0;
  const conv = visits > 0 ? leads / visits : 0;
  const publishedAt = page.published_at ? new Date(page.published_at) : null;
  const daysSince = publishedAt ? Math.max(1, Math.ceil((Date.now() - publishedAt.getTime()) / 86400000)) : 1;
  const pace = +(leads / daysSince).toFixed(1);
  const totalDays = daysLeft != null ? daysSince + Math.max(0, daysLeft) : null;
  const proj = totalDays ? Math.round(leads / daysSince * totalDays) : null;

  // Courbe : participations par jour (14 derniers jours)
  const byDay = {};
  (stats.leads || []).forEach(l => {
    const d = (l.created_at || '').slice(0, 10);
    if (d) byDay[d] = (byDay[d] || 0) + 1;
  });
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    days.push({ iso, n: byDay[iso] || 0, lbl: d.toLocaleDateString('fr-CH', { day: 'numeric' }) });
  }
  const maxN = Math.max(1, ...days.map(d => d.n));

  // 🧞 Conseils proactifs — mêmes règles que l'e-mail de pouls
  const prize = (page.config?.prize_name || 'ton lot').replace(/^[^\p{L}\p{N}]+\s*/u, '');
  let mood, verdict, advices;
  if (isDone) {
    mood = '🏁'; verdict = 'Concours terminé — bravo !';
    advices = ["Annonce le gagnant sur tes réseaux (reel = LE contenu le plus partagé)", `Les ${leads} participants sont dans ta base, consentis, anniversaires actifs — les vœux automatiques prennent le relais`, 'Remets un concours au calendrier dans 3-4 mois : une base se cultive'];
  } else if (visits === 0) {
    mood = '🚨'; verdict = "Personne n'a encore vu ta page";
    advices = ["Poste l'annonce Facebook/Instagram (textes prêts ci-dessous)", "Imprime l'affiche QR et pose-la à la caisse dès demain", 'Mets le lien en bio Insta/TikTok + une story aujourd\'hui'];
  } else if (visits >= 20 && conv < 0.15) {
    mood = '🔧'; verdict = `Ta page est vue mais convertit mal (${Math.round(conv * 100)}% de participation)`;
    advices = [`Le lot est-il assez désirable ? Mets « ${prize} » plus en avant (✏️ Modifier)`, 'Le formulaire doit se remplir en 10 secondes — simplifie', 'Rappelle la date du tirage dans tes posts (urgence)'];
  } else if (conv >= 0.35 || (proj != null && proj >= 30)) {
    mood = '🎉'; verdict = proj ? `Ça décolle — en route vers ~${proj} participants !` : 'Ça décolle — superbe taux de participation !';
    advices = [`Amplifie tant que c'est chaud : story compte à rebours + post « déjà ${leads} participants »`, '10-20 CHF de pub Facebook locale sur le post d\'annonce = multiplicateur', "Prépare le reel d'annonce du gagnant dès maintenant"];
  } else {
    mood = '👍'; verdict = `Ton concours avance (${pace} participant${pace > 1 ? 's' : ''}/jour)`;
    advices = ['Reposte l\'annonce à une autre heure (le soir touche d\'autres gens que le matin)', 'Demande à 2-3 clients fidèles de partager le lien', "L'affiche QR au comptoir est-elle posée ? Elle travaille pendant que tu bosses"];
  }

  return (
    <div className="acq-card">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 900, fontSize: 15, color: 'var(--ink)' }}>📊 Rapport du concours</span>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>mis à jour en direct · pouls envoyé par e-mail à J+3, mi-parcours et J-2</span>
      </div>
      <div className="acq-report-grid">
        <div className="acq-report-kpis">
          {[['👀', visits, 'visites'], ['🎂', leads, 'participants'], ['🎯', visits > 0 ? `${Math.round(conv * 100)}%` : '—', visits > 0 && visits < 20 ? 'participation (petit échantillon)' : 'participation'], ['📈', proj ?? '—', 'projection fin']].map(([ic, v, l]) => (
            <div key={l} className="acq-kpi"><span className="acq-kpi-ic">{ic}</span><b>{v}</b><span>{l}</span></div>
          ))}
        </div>
        <div className="acq-chart">
          {days.map(d => (
            <div key={d.iso} className="acq-chart-col" title={`${d.n} participation${d.n > 1 ? 's' : ''}`}>
              <div className="acq-chart-bar" style={{ height: `${Math.round(d.n / maxN * 64)}px`, opacity: d.n ? 1 : .25 }} />
              <span className="acq-chart-lbl">{d.lbl}</span>
            </div>
          ))}
        </div>
      </div>
      {(() => {
        // 📡 Attribution : visites par canal (config) + participations par canal (leads)
        const chStats = page.config?.channel_stats || {};
        const leadsBySrc = {};
        (stats.leads || []).forEach(l => { const k = l.source_channel || 'direct'; leadsBySrc[k] = (leadsBySrc[k] || 0) + 1; });
        const allKeys = [...new Set([...Object.keys(chStats), ...Object.keys(leadsBySrc)])].filter(k => (chStats[k]?.visits || 0) + (leadsBySrc[k] || 0) > 0);
        if (!allKeys.length) return null;
        const totalLeads = Object.values(leadsBySrc).reduce((a, b) => a + b, 0) || 1;
        const rows = allKeys.map(k => ({
          k, label: CHANNELS[k] || k,
          visits: chStats[k]?.visits || 0,
          leads: leadsBySrc[k] || 0,
          share: Math.round((leadsBySrc[k] || 0) / totalLeads * 100),
          conv: (chStats[k]?.visits || 0) > 0 ? Math.round((leadsBySrc[k] || 0) / chStats[k].visits * 100) : null,
        })).sort((a, b) => b.leads - a.leads);
        const best = rows.filter(r => r.conv != null && r.visits >= 5).sort((a, b) => b.conv - a.conv)[0];
        return (
          <div style={{ marginTop: 14, background: '#fff', border: '1.5px solid var(--line)', borderRadius: 14, padding: '12px 16px' }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 8 }}>📡 D'où viennent tes participants</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rows.map(r => (
                <div key={r.k} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                  <span style={{ width: 100, fontWeight: 700, color: '#374151' }}>{r.label}</span>
                  <div style={{ flex: 1, height: 9, background: 'var(--violet-soft)', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${r.share}%`, background: 'var(--violet)', borderRadius: 5 }} />
                  </div>
                  <span style={{ width: 130, textAlign: 'right', color: 'var(--muted)' }}><b style={{ color: 'var(--ink)' }}>{r.share}%</b> · {r.leads} inscrit{r.leads > 1 ? 's' : ''}{r.conv != null ? ` · ${r.conv}% conv.` : ''}</span>
                </div>
              ))}
            </div>
            {best && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                🧞 <b style={{ color: 'var(--ink)' }}>{best.label}</b> convertit le mieux ({best.conv}%) — remets ton énergie (et tes prochains posts) là-bas.
              </div>
            )}
          </div>
        );
      })()}
      {(() => {
        // 🚀 Parrainage : podium des ambassadeurs + part virale
        const leadsAll = stats.leads || [];
        const referred = leadsAll.filter(l => l.referred_by_lead_id).length;
        const ambassadors = leadsAll.filter(l => (l.chances || 1) > 1)
          .map(l => ({ name: [l.first_name, l.last_name].filter(Boolean).join(' ') || l.email, filleuls: (l.chances || 1) - 1 }))
          .sort((a, b) => b.filleuls - a.filleuls).slice(0, 3);
        if (!referred && !ambassadors.length) return null;
        const share = leadsAll.length ? Math.round(referred / leadsAll.length * 100) : 0;
        const maxF = Math.max(1, ...ambassadors.map(a => a.filleuls));
        return (
          <div style={{ marginTop: 14, background: '#fff', border: '1.5px solid var(--line)', borderRadius: 14, padding: '12px 16px' }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 8 }}>🚀 Parrainage — tes ambassadeurs</div>
            {ambassadors.map((a, i) => (
              <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '4px 0' }}>
                <span>{['🥇', '🥈', '🥉'][i]}</span>
                <span style={{ width: 130, fontWeight: 700, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                <div style={{ flex: 1, height: 8, background: 'var(--violet-soft)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(a.filleuls / maxF * 100)}%`, background: 'var(--violet)', borderRadius: 4 }} />
                </div>
                <b style={{ width: 70, textAlign: 'right' }}>{a.filleuls} filleul{a.filleuls > 1 ? 's' : ''}</b>
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              🧞 <b style={{ color: 'var(--ink)' }}>{share}% de tes participants viennent du parrainage</b>{share >= 20 ? ' — tes clients sont tes meilleurs vendeurs. Pense à remercier ton n°1 !' : ' — encourage le partage : mentionne les 5 chances dans tes stories.'}
            </div>
          </div>
        );
      })()}
      <div className="acq-genie">
        <div className="acq-genie-verdict">{mood} <b>{verdict}</b></div>
        <ol className="acq-genie-list">
          {advices.map((a, i) => <li key={i}>{a}</li>)}
        </ol>
      </div>
    </div>
  );
}

/* ── 📄 Procès-verbal du tirage — imprimable (Ctrl+P → PDF) ──────────────── */
function PvModal({ draw, page, onClose }) {
  return createPortal(
    <div className="acq-winner-backdrop" onClick={onClose}>
      <div className="acq-winner-card" style={{ maxWidth: 560, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 34 }}>📄</div>
          <h2 style={{ margin: '6px 0 2px', fontSize: 20, fontWeight: 900 }}>Procès-verbal de tirage au sort</h2>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{page.title} · identifiant de contrôle <b>{draw.controle}</b></div>
        </div>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Date et heure du tirage', new Date(draw.at).toLocaleString('fr-CH')],
              ['Méthode', draw.methode],
              ['Participations reçues', draw.participants],
              ['Participations valides', draw.valides],
              ['Exclusions', (draw.exclusions || []).length ? (draw.exclusions || []).map(e => `#${e.id} (${e.raison})`).join(', ') : 'aucune'],
              ['Suppléants désignés', (draw.suppleants || []).map(sp => sp.nom || sp.email).join(', ') || '—'],
              ...(draw.remplacements || []).map((r, i) => [`Remplacement ${i + 1}`, new Date(r.at).toLocaleString('fr-CH')]),
            ].map(([k, v]) => (
              <tr key={k}><td style={{ padding: '7px 8px', borderBottom: '1px solid var(--line)', color: 'var(--muted)', width: 190 }}>{k}</td><td style={{ padding: '7px 8px', borderBottom: '1px solid var(--line)' }}>{v}</td></tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
          Document généré par Appee. L'identifiant de contrôle est calculé à partir du tirage — il atteste que ce procès-verbal correspond bien à ce tirage précis.
        </div>
        <div className="acq-winner-actions">
          <button className="acq-btn acq-btn--primary" onClick={() => window.print()}>🖨 Imprimer / PDF</button>
          <button className="acq-btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── 📊 Accueil du module : KPIs 30 jours agrégés + phrase interprétative ── */
function AcqKpis({ pages }) {
  const [agg, setAgg] = useState(null);
  useEffect(() => {
    const list = (pages || []).filter(p => p.id);
    if (!list.length) { setAgg(null); return; }
    Promise.all(list.map(p => api.getLandingPageStats(p.id).catch(() => null))).then(all => {
      const cut = Date.now() - 30 * 86400000;
      let leads30 = 0, consentEmail = 0, couponsUsed = 0, total = 0;
      all.forEach(st => (st?.leads || []).forEach(l => {
        total++;
        if (new Date(l.created_at).getTime() >= cut) leads30++;
        if (l.consents?.email?.ok) consentEmail++;
        if (l.coupon_redeemed_at) couponsUsed++;
      }));
      setAgg({ leads30, consentEmail, couponsUsed, total });
    });
  }, [pages]);

  if (!agg || agg.total === 0) return null;
  const phrase = agg.leads30 > 0
    ? `${agg.leads30} nouveau${agg.leads30 > 1 ? 'x' : ''} contact${agg.leads30 > 1 ? 's' : ''} collecté${agg.leads30 > 1 ? 's' : ''} en 30 jours — ta base grandit toute seule.`
    : `Aucune collecte ces 30 derniers jours — un commerce comme le tien peut gagner 20 à 60 contacts par mois. Relance une campagne !`;
  return (
    <div className="acq-card" style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', padding: '13px 18px' }}>
      {[['🧲', agg.leads30, 'contacts / 30j'], ['📧', agg.consentEmail, 'consentements e-mail'], ['🎟', agg.couponsUsed, 'visites mesurées'], ['👥', agg.total, 'collectés au total']].map(([ic, v, l]) => (
        <div key={l} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 15 }}>{ic}</span>
          <b style={{ fontSize: 19, color: 'var(--ink)' }}>{v}</b>
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{l}</span>
        </div>
      ))}
      <span style={{ flexBasis: '100%', fontSize: 12, color: 'var(--muted)' }}>🧞 {phrase}</span>
    </div>
  );
}